
import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Article } from '../../../utils/articleApi';
import ArticleCategoryBadges from '../../../components/feature/ArticleCategoryBadges';
import AuthorAvatar from '../../../components/feature/AuthorAvatar';

interface RelatedArticlesProps {
  /** 親で fetchPublishedArticles 済みの一覧（再取得しない） */
  articles: Article[];
}

/** 記事詳細「関連旅行記事」の最大表示件数（マーケ要件: 最大4件） */
export const RELATED_ARTICLES_MAX = 4;

export default function RelatedArticles({ articles }: RelatedArticlesProps) {
  const { id } = useParams<{ id: string }>();

  const relatedArticles = useMemo(() => {
    const published = articles.filter((a) => a.status === '公開');
    return published
      .filter((article) => String(article.id) !== String(id))
      .slice(0, RELATED_ARTICLES_MAX);
  }, [articles, id]);

  return (
    <section className="bg-[#F8FAFC] py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-[#111827] mb-8">関連旅行記事</h2>

        <div className="grid md:grid-cols-2 gap-8">
          {relatedArticles.map((article) => (
            <Link
              key={article.id}
              to={`/articles/${article.id}`}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300"
            >
              <div className="aspect-video overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
              </div>

              <div className="p-6">
                <div className="mb-3">
                  <ArticleCategoryBadges article={article} badgeClassName="inline-block bg-[#14B8A6] text-white px-3 py-1 rounded-full text-sm font-medium" />
                </div>

                <h3 className="text-xl font-bold text-[#111827] mb-3 group-hover:text-[#14B8A6] transition-colors line-clamp-2">
                  {article.title}
                </h3>

                <p className="text-[#6B7280] mb-4 line-clamp-2">
                  {article.excerpt}
                </p>

                <div className="flex items-center justify-between text-sm text-[#6B7280]">
                  <div className="flex items-center space-x-2">
                    <AuthorAvatar size="sm" />
                    <span>{article.author}</span>
                  </div>
                  <span>{article.readTime}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/articles"
            className="inline-flex items-center space-x-2 bg-[#14B8A6] text-white px-8 py-3 rounded-lg hover:bg-[#0F9488] transition-colors font-medium"
          >
            <span>すべての旅行記事を見る</span>
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
